import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const infoTooltipVariants = cva(
  "flex items-center justify-center gap-[0.625rem] rounded-[0.3125rem] border border-solid box-border transition-colors",
  {
    variants: {
      variant: {
        default: "bg-gray-50 border-gray-100 text-gray-700",
        info: "bg-gray-50 border-gray-100 text-gray-700",
        warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
        error: "bg-red-100 border-red-200 text-red-900",
        success: "bg-green-100 border-green-200 text-green-900",
      },
      size: {
        default: "p-4",
        sm: "p-2",
        lg: "p-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface InfoTooltipProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof infoTooltipVariants> {
  /**
   * Icon to display before the message (optional)
   */
  icon?: React.ReactNode
}

/**
 * InfoTooltip component for displaying informational messages.
 *
 * Based on Figma design: info : My profil réseaux
 * Node ID: 726:7690
 *
 * @example
 * <InfoTooltip>
 *   If you do not wish to display this network, leave the field empty.
 * </InfoTooltip>
 *
 * @example
 * <InfoTooltip variant="warning">
 *   This action cannot be undone.
 * </InfoTooltip>
 */
const InfoTooltip = React.forwardRef<HTMLDivElement, InfoTooltipProps>(
  ({ className, variant, size, icon, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(infoTooltipVariants({ variant, size, className }))}
        role="status"
        aria-live="polite"
        {...props}
      >
        {icon && <span className="inline-flex items-center flex-shrink-0">{icon}</span>}
        <p className="flex-1 font-montserrat font-medium text-[0.75rem] leading-normal text-center whitespace-pre-wrap min-w-0">
          {children}
        </p>
      </div>
    )
  }
)
InfoTooltip.displayName = "InfoTooltip"

export { InfoTooltip, infoTooltipVariants }
